import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException as reportToSentry } from '@sentry/react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { IsNotEmpty, IsOptional, IsString, MinLength } from 'class-validator';
import { WarningIconSVG } from 'src/assets/svg';
import { Button } from 'src/components/Buttons/Button';
import Form from 'src/components/FormComponents/Form';
import Input from 'src/components/FormComponents/Input';
import PasswordInput from 'src/components/FormComponents/PasswordInput';
import { SlideList } from 'src/components/onboarding/Onboarding';
import { Modal } from 'src/components/Modals/Modal';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { AuthError } from 'src/api/auth.service';
import { User, UserRole, getPrimaryRole, isUserAdmin } from 'src/domain/user';
import { UserActions } from 'src/state/_actions';
import { Screen } from 'src/view/routes/Router';
import { LandingPageHeader } from 'src/view/screens/Mundu/components';
import { ActivateAccountState } from './ActivateAccount';
import { AccessSession } from 'src/components/AccountAccess/accessErrors';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import type { UseFormReturn } from 'react-hook-form';

const {
    cradle: { authService, munduClient },
} = container;

class LoginSchema {
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    identifier: string;

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @MinLength(6, { message: () => i18n.t('validation.passwordTooShort') })
    password: string;

    @IsOptional()
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @MinLength(6, { message: () => i18n.t('validation.passwordTooShort') })
    newPassword?: string;

    @IsOptional()
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @MinLength(6, { message: () => i18n.t('validation.passwordTooShort') })
    confirmNewPassword?: string;
}

const ONBOARDING_KEY_PREFIX = 'mundu-onboarding-dismissed-';

const hasSeenOnboarding = (userId?: string) => {
    if (!userId) return false;
    try {
        return (
            localStorage.getItem(`${ONBOARDING_KEY_PREFIX}${userId}`) === 'true'
        );
    } catch (error) {
        console.warn('Unable to read onboarding state', error);
        return false;
    }
};

const markOnboardingSeen = (userId?: string) => {
    if (!userId) return;
    try {
        localStorage.setItem(`${ONBOARDING_KEY_PREFIX}${userId}`, 'true');
    } catch (error) {
        console.warn('Unable to persist onboarding state', error);
    }
};

const deriveHomePath = (user?: User) => {
    if (!user) return '/';
    if (isUserAdmin(user)) {
        return '/app/admin';
    }
    const role = getPrimaryRole(user);
    switch (role) {
        case UserRole.organization:
        case UserRole.organizationB2C:
        case UserRole.creator:
            return '/app/home';
        case UserRole.nextOfKin:
            return '/app/nextofkin-image-list';
        default:
            return '/';
    }
};

export const Login = () => {
    const history = useHistory();
    const notify = useNotification();
    const dispatch = useDispatch();
    const [onboardingComplete, setOnboardingComplete] = useState(true);
    const [user, setUser] = useState<User>();
    const [formError, setFormError] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isBootstrapping, setIsBootstrapping] = useState(true);
    const [pendingPasswordChange, setPendingPasswordChange] = useState<
        { identifier: string; password: string } | undefined
    >();
    const { t } = useTranslation();
    const formRef = useRef<UseFormReturn<LoginSchema> | null>(null);
    const passwordChangeRequired = Boolean(pendingPasswordChange);

    const shouldShowOnboarding = useCallback((userId?: string) => {
        const account = authService.getActiveAccount();
        const showFromToken = account?.idTokenClaims?.showOnboarding === true;
        if (!showFromToken) return false;
        if (!userId) return showFromToken;
        return !hasSeenOnboarding(userId);
    }, []);

    const initializeSession = useCallback(async () => {
        const fetchedUser = await munduClient.getUser();
        dispatch(UserActions.setUser(fetchedUser));
        setUser(fetchedUser);
        setOnboardingComplete(!shouldShowOnboarding(fetchedUser.id));
    }, [dispatch, shouldShowOnboarding]);

    useEffect(() => {
        if (!authService.isAuthenticated()) {
            setIsBootstrapping(false);
            return;
        }

        initializeSession()
            .catch(error => {
                authService.logout();
                dispatch(UserActions.clearUserState());
                reportToSentry(error as Error);
            })
            .finally(() => setIsBootstrapping(false));
    }, [dispatch, initializeSession]);

    useEffect(() => {
        if (!user) return;
        if (!onboardingComplete) return;

        const timeout = setTimeout(() => {
            const path = deriveHomePath(user);
            if (path === '/') {
                sessionStorage.clear();
                return;
            }
            history.replace(path);
        }, 500);

        return () => clearTimeout(timeout);
    }, [history, onboardingComplete, user]);

    const headerColors = useMemo(
        () => ({
            textColor: 'woodyBrown',
            activeTextColor: 'mongooseGray',
            backgroundColor: 'lightPeach',
        }),
        [],
    );

    const handleSubmit = useCallback(
        async (values: LoginSchema) => {
            setFormError(undefined);

            if (pendingPasswordChange) {
                if (!values.newPassword || !values.confirmNewPassword) {
                    setFormError(t('validation.requiredField'));
                    return;
                }

                if (values.newPassword !== values.confirmNewPassword) {
                    setFormError(t('validation.passwordsMismatch'));
                    return;
                }
            }

            setIsSubmitting(true);

            const rawIdentifier = values.identifier.trim();
            const identifierSource = pendingPasswordChange
                ? pendingPasswordChange.identifier
                : rawIdentifier;
            const passwordSource = pendingPasswordChange
                ? pendingPasswordChange.password
                : values.password;
            const useEmail = identifierSource.includes('@');
            const phoneInput = identifierSource.replace(/\s/g, '');
            const usePhoneNumber = /^\+?\d+$/.test(phoneInput);
            const normalizedIdentifier = useEmail
                ? identifierSource.toLowerCase()
                : identifierSource;
            const credentials = useEmail
                ? { email: normalizedIdentifier, password: passwordSource }
                : usePhoneNumber
                ? {
                      phoneNumber: phoneInput.startsWith('+')
                          ? phoneInput
                          : `+47${phoneInput}`,
                      password: passwordSource,
                  }
                : { username: normalizedIdentifier, password: passwordSource };
            const newPassword = pendingPasswordChange
                ? values.newPassword
                : undefined;

            try {
                await authService.loginWithCredentials(
                    credentials,
                    newPassword,
                );
                await initializeSession();
                setPendingPasswordChange(undefined);
                formRef.current?.setValue('newPassword', '');
                formRef.current?.setValue('confirmNewPassword', '');
            } catch (error) {
                if (error instanceof AuthError) {
                    if (!error.status || error.status >= 500) {
                        reportToSentry(error);
                    }
                    if (error.code === 'INVITATION_CODE_RESENT') {
                        // The invitation code had expired; a new one was sent.
                        const state: ActivateAccountState = {
                            identifier: identifierSource,
                            session: error.details?.session as AccessSession,
                            notice: 'codeExpiredResent',
                        };
                        history.push('/activate', state);
                        return;
                    }
                    if (error.code === 'SIGN_IN_REQUIRED') {
                        // The password is saved; only the automatic sign-in failed.
                        setPendingPasswordChange(undefined);
                        formRef.current?.setValue('password', '');
                        formRef.current?.setValue('newPassword', '');
                        formRef.current?.setValue('confirmNewPassword', '');
                        notify(t('accountAccess.errors.SIGN_IN_REQUIRED'));
                        return;
                    }
                    if (error.code === 'CONTACT_NOT_VERIFIED') {
                        setFormError(
                            t('accountAccess.errors.CONTACT_NOT_VERIFIED'),
                        );
                        return;
                    }
                    if (error.code === 'ACTIVATION_REQUIRED') {
                        const state: ActivateAccountState = {
                            identifier: identifierSource,
                        };
                        history.push('/activate', state);
                        return;
                    }
                    if (error.code === 'PASSWORD_CHANGE_REQUIRED') {
                        setPendingPasswordChange({
                            identifier: normalizedIdentifier,
                            password: passwordSource,
                        });
                        formRef.current?.setValue(
                            'identifier',
                            normalizedIdentifier,
                            { shouldDirty: false },
                        );
                        formRef.current?.setValue('password', passwordSource, {
                            shouldDirty: false,
                        });
                        formRef.current?.setValue('newPassword', '');
                        formRef.current?.setValue('confirmNewPassword', '');
                        setFormError(t('login.passwordChangeRequired'));
                    } else {
                        setFormError(error.message);
                    }
                } else if (error instanceof Error) {
                    reportToSentry(error);
                    setFormError(t('login.genericError'));
                } else {
                    setFormError(t('login.genericError'));
                }
            } finally {
                setIsSubmitting(false);
                setIsBootstrapping(false);
            }
        },
        [history, initializeSession, notify, pendingPasswordChange, t],
    );

    const resetPasswordChangeFlow = useCallback(() => {
        setPendingPasswordChange(undefined);
        setFormError(undefined);
        formRef.current?.setValue('newPassword', '');
        formRef.current?.setValue('confirmNewPassword', '');
    }, []);

    return (
        <div className="bg-fixed min-h-screen flex flex-col">
            <LandingPageHeader
                colors={headerColors}
                scrollItems={false}
                navItems={[{ id: Screen.Mundu, name: t('nav.home') }]}
                languageSelect
            />
            <div className="grow flex justify-center items-center px-6 py-10">
                <div className="flex flex-col gap-6 justify-center items-center p-10 sm:shadow-md max-w-lg w-full rounded-2xl bg-starkWhite/80">
                    <div className="flex flex-col items-center gap-2 text-center">
                        <div className="font-bold text-2xl">
                            {t('login.title')}
                        </div>
                        <div className="text-mongooseGray text-base">
                            {t('login.description')}
                        </div>
                    </div>
                    <Form
                        schema={LoginSchema}
                        onSubmit={handleSubmit}
                        mode="onChange"
                        className="w-full"
                    >
                        {methods => {
                            formRef.current = methods;
                            const {
                                register,
                                formState: { errors, isValid },
                                watch,
                            } = methods;
                            const newPasswordValue = watch('newPassword');
                            const confirmNewPasswordValue =
                                watch('confirmNewPassword');
                            const needsNewPassword =
                                passwordChangeRequired &&
                                (!newPasswordValue || !confirmNewPasswordValue);
                            const passwordsDoNotMatch =
                                passwordChangeRequired &&
                                Boolean(newPasswordValue) &&
                                Boolean(confirmNewPasswordValue) &&
                                newPasswordValue !== confirmNewPasswordValue;
                            const isButtonDisabled =
                                !isValid ||
                                isSubmitting ||
                                isBootstrapping ||
                                needsNewPassword ||
                                passwordsDoNotMatch;
                            const buttonLabel = isSubmitting
                                ? t('login.submitting')
                                : passwordChangeRequired
                                ? t('login.submitNewPassword')
                                : t('login.submit');

                            return (
                                <div className="flex flex-col gap-4 w-full">
                                    <Input
                                        className="text-input"
                                        label={t('login.identifierLabel')}
                                        error={errors.identifier}
                                        disabled={
                                            isSubmitting ||
                                            isBootstrapping ||
                                            passwordChangeRequired
                                        }
                                        {...register('identifier')}
                                        autoComplete="username"
                                    />
                                    <PasswordInput
                                        className="text-input"
                                        label={t('login.passwordLabel')}
                                        error={errors.password}
                                        disabled={
                                            isSubmitting ||
                                            isBootstrapping ||
                                            passwordChangeRequired
                                        }
                                        autoComplete="current-password"
                                        {...register('password')}
                                    />
                                    <div className="flex justify-end">
                                        <Link
                                            to="/reset-password"
                                            className="text-sm text-woodyBrown underline underline-offset-2"
                                        >
                                            {t('login.forgotPassword')}
                                        </Link>
                                    </div>
                                    {passwordChangeRequired && (
                                        <div className="flex flex-col gap-3 rounded-xl bg-lightPeach/60 p-4">
                                            <p className="text-sm text-mongooseGray">
                                                {t(
                                                    'login.newPasswordDescription',
                                                )}
                                            </p>
                                            <PasswordInput
                                                className="text-input"
                                                label={t(
                                                    'login.newPasswordLabel',
                                                )}
                                                autoComplete="new-password"
                                                error={errors.newPassword}
                                                disabled={
                                                    isSubmitting ||
                                                    isBootstrapping
                                                }
                                                {...register('newPassword')}
                                            />
                                            <PasswordInput
                                                className="text-input"
                                                label={t(
                                                    'login.confirmNewPasswordLabel',
                                                )}
                                                autoComplete="new-password"
                                                error={
                                                    errors.confirmNewPassword
                                                }
                                                disabled={
                                                    isSubmitting ||
                                                    isBootstrapping
                                                }
                                                {...register(
                                                    'confirmNewPassword',
                                                )}
                                            />
                                            {passwordsDoNotMatch && (
                                                <p className="text-sm text-venetianRed">
                                                    {t(
                                                        'validation.passwordsMismatch',
                                                    )}
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                className="text-left text-sm text-woodyBrown underline underline-offset-2"
                                                onClick={
                                                    resetPasswordChangeFlow
                                                }
                                                disabled={isSubmitting}
                                            >
                                                {t('login.useDifferentAccount')}
                                            </button>
                                        </div>
                                    )}
                                    {formError && (
                                        <div className="flex items-center gap-2 text-venetianRed text-sm">
                                            <WarningIconSVG className="w-4 h-4 shrink-0" />
                                            <span>{formError}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-center">
                                        <Button
                                            title={buttonLabel}
                                            submit
                                            disabled={isButtonDisabled}
                                        />
                                    </div>
                                </div>
                            );
                        }}
                    </Form>
                </div>
            </div>
            <Modal
                show={onboardingComplete === false}
                fadeDuration={500}
                appear={false}
                className="fixed inset-0 z-70 h-screen w-screen bg-woodyBrown"
            >
                <SlideList
                    onComplete={() => {
                        markOnboardingSeen(user?.id);
                        setOnboardingComplete(true);
                    }}
                />
            </Modal>
        </div>
    );
};
